import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  effect,
  forwardRef,
  ElementRef,
  viewChild,
  HostListener,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface MultiSelectOption {
  id: any;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-custom-multiselect',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './custom-multiselect.component.html',
  styleUrls: ['./custom-multiselect.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomMultiselectComponent),
      multi: true,
    },
  ],
})
export class CustomMultiselectComponent implements ControlValueAccessor {
  options = input<MultiSelectOption[]>([]);
  placeholder = input<string>('Select...');
  disabledInput = input(false, { alias: 'disabled' });
  private _disabledFromCVA = signal<boolean>(false);
  disabled = computed(() => this.disabledInput() || this._disabledFromCVA());
  multiple = input<boolean>(true);
  searchable = input<boolean>(true);
  maxHeight = input<string>('300px');
  selected = input<any[]>([]);

  selectionChange = output<any[]>();

  isOpen = signal<boolean>(false);
  searchTerm = signal<string>('');
  selectedValues = signal<any[]>([]);

  searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  filteredOptions = computed(() => {
    const term = this.searchTerm().toLowerCase();
    const opts = this.options();
    if (!term) return opts;
    return opts.filter(opt => opt.label.toLowerCase().includes(term));
  });

  selectedOptions = computed(() => {
    const selected = this.selectedValues();
    const opts = this.options();
    return opts.filter(opt => this.containsId(selected, opt.id));
  });

  displayText = computed(() => {
    const selected = this.selectedOptions();
    if (selected.length === 0) return this.placeholder();
    if (!this.multiple() && selected.length === 1) return selected[0].label;
    if (this.multiple() && selected.length > 5) {
      return `${selected.length} items selected`;
    }
    return selected.map(s => s.label).join(', ');
  });

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Keep internal state in sync with parent `[selected]` only when it actually changes.
    // `selectedValues` is read untracked so user-driven selection does not re-trigger
    // this effect and get reset back to the `[selected]` input.
    effect(() => {
      const extSelected = this.selected();
      if (!Array.isArray(extSelected)) {
        return;
      }

      if (untracked(() => this.sameSelection(this.selectedValues(), extSelected))) {
        return;
      }

      this.selectedValues.set([...extSelected]);
    });
  }

  selectAllVisible(): void {
    const allIds = this.filteredOptions()
      .filter(opt => !opt.disabled)
      .map(opt => opt.id);
    this.commitSelection(allIds);
  }

  clearAllVisible(): void {
    this.commitSelection([]);
  }

  toggleSelectAll(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.areAllVisibleSelected()) {
      this.clearAllVisible();
    } else {
      this.selectAllVisible();
    }
  }

  areAllVisibleSelected(): boolean {
    const visibleIds = this.filteredOptions()
      .filter(opt => !opt.disabled)
      .map(opt => opt.id);
    return visibleIds.length > 0 && visibleIds.every(id => this.containsId(this.selectedValues(), id));
  }

  toggleOption(option: MultiSelectOption, event?: Event): void {
    if (option.disabled) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const currentValues = [...this.selectedValues()];
    const index = currentValues.findIndex(value => this.idsEqual(value, option.id));

    if (this.multiple()) {
      if (index > -1) {
        currentValues.splice(index, 1);
      } else {
        currentValues.push(option.id);
      }
      this.commitSelection(currentValues);
    } else {
      this.commitSelection([option.id]);
      this.closeDropdown();
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.isOpen()) {
      this.closeDropdown();
    }
  }

  toggleDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.disabled()) return;

    const newState = !this.isOpen();
    this.isOpen.set(newState);

    if (newState && this.searchable()) {
      setTimeout(() => {
        const input = this.searchInput();
        if (input) {
          input.nativeElement.focus();
        }
      }, 50);
    } else {
      this.searchTerm.set('');
    }
  }

  closeDropdown(): void {
    this.isOpen.set(false);
    this.searchTerm.set('');
    this.onTouched();
  }

  isSelected(id: any): boolean {
    return this.containsId(this.selectedValues(), id);
  }

  removeItem(id: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const currentValues = this.selectedValues().filter(value => !this.idsEqual(value, id));
    this.commitSelection(currentValues);
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.searchTerm.set(target?.value ?? '');
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  writeValue(value: any): void {
    if (Array.isArray(value)) {
      this.selectedValues.set([...value]);
    } else if (value != null) {
      this.selectedValues.set([value]);
    } else {
      this.selectedValues.set([]);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledFromCVA.set(isDisabled);
  }

  private commitSelection(values: any[]): void {
    this.selectedValues.set([...values]);
    this.selectionChange.emit([...values]);
    this.onChange(this.multiple() ? [...values] : values[0] ?? null);
  }

  private containsId(values: any[], id: any): boolean {
    return values.some(value => this.idsEqual(value, id));
  }

  private sameSelection(a: any[], b: any[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    return a.every(value => this.containsId(b, value));
  }

  private idsEqual(a: any, b: any): boolean {
    if (a === b) {
      return true;
    }
    if (a == null || b == null) {
      return false;
    }
    return String(a) === String(b);
  }
}
